import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronLeft, ChevronRight, Download, Target, ShieldAlert, Megaphone, CalendarDays, Settings2, Flame, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useRepFilter } from "@/context/RepFilterContext";
import { RISK_TONE } from "@/components/FacilityRiskCard";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const shift = (iso, period, dir) => {
  const d = new Date(`${iso}T00:00:00`);
  if (period === "quarter") d.setMonth(d.getMonth() + 3 * dir); else if (period === "month") d.setMonth(d.getMonth() + dir); else d.setDate(d.getDate() + 7 * dir);
  return d.toISOString().slice(0, 10);
};
const fmtRange = (ws) => ws.period === "quarter"
  ? `Q${Math.floor(new Date(`${ws.start}T00:00:00`).getMonth() / 3) + 1} ${ws.start.slice(0, 4)} · ${ws.start} → ${ws.end}`
  : ws.period === "month"
  ? new Date(`${ws.start}T00:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" })
  : `${ws.start} → ${ws.end}`;
const PERIOD_LABEL = { week: "Weekly", month: "Monthly", quarter: "Quarterly" };

const METRICS = [
  { key: "active_visits", label: "Active facility visits", target: "active_visits", tone: "bg-emerald-600", plan: "active_visit", grad: "from-emerald-500 to-teal-600" },
  { key: "prospect_visits", label: "Prospect / churn visits", target: "prospect_visits", tone: "bg-amber-500", plan: "prospect_visit", grad: "from-amber-400 to-orange-500" },
  { key: "provider_meetings", label: "Provider meetings", target: "provider_meetings", tone: "bg-indigo-600", plan: "provider_meeting", grad: "from-indigo-500 to-violet-600" },
  { key: "at_risk_count", label: "At-risk escalated", tone: "bg-rose-600", grad: "from-rose-500 to-pink-600" },
  { key: "followups_done", label: "Follow-ups completed", target: "followups", sub: (r) => `${r.followups_due} due · ${r.followups_open} open`, tone: "bg-violet-600", plan: "followup", grad: "from-fuchsia-500 to-purple-600" },
  { key: "marketing_count", label: "Marketing activities", target: "marketing", sub: (r) => `${money(r.marketing_spent)} spent · ${money(r.marketing_revenue)} rev`, tone: "bg-sky-600", plan: "marketing", grad: "from-sky-500 to-cyan-500" },
];

function ScoreTiles({ ws, rows, isAdmin, userId }) {
  const mine = rows.find(r => r.rep_id === userId);
  const scope = rows.length === 1 ? rows[0] : (isAdmin ? ws.team : mine);
  if (!scope) return null;
  const targets = rows.length === 1 ? rows[0].targets : (isAdmin ? ws.team_targets : mine?.targets);
  const label = rows.length === 1 ? rows[0].rep_name : "Team";
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4" data-testid="ws-tiles">
      {METRICS.map(m => {
        const target = m.target ? targets?.[m.target] ?? null : null;
        const value = scope[m.key] || 0;
        const pct = target ? Math.min(100, Math.round((value / target) * 100)) : null;
        const hit = target != null && value >= target;
        return (
          <div key={m.key} className={`relative overflow-hidden rounded-xl p-4 text-white bg-gradient-to-br ${m.grad} shadow-md hover:shadow-lg hover:-translate-y-[2px] transition-all`} data-testid={`ws-tile-${m.key}`}>
            <div className="text-[10px] uppercase tracking-widest font-mono-plex text-white/85 truncate">{m.label}</div>
            <div className="mt-2 flex items-baseline gap-1.5"><span className="font-heading font-black text-4xl leading-none drop-shadow-sm">{value}</span>{target != null && <span className="text-[11px] text-white/85 font-mono-plex">/ {target}</span>}</div>
            {pct != null && <div className="mt-2 h-1.5 rounded-full bg-white/25 overflow-hidden"><div className="h-full bg-white/90 rounded-full" style={{ width: `${pct}%` }} /></div>}
            <div className="mt-1.5 text-[11px] font-mono-plex text-white/90 flex items-center justify-between gap-2">
              <span className="truncate">{label}</span>
              {target != null && <span className="px-1.5 py-0.5 rounded-full bg-white/25 font-bold">{hit ? "on target" : `${target - value} to go`}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MetricCell({ value, target, tone, sub, testId, planned, planType, start }) {
  const pct = target ? Math.min(100, Math.round((value / target) * 100)) : null;
  const hit = target != null && value >= target;
  return (
    <div data-testid={testId}>
      <div className="flex items-baseline gap-1">
        <span className={`font-heading font-black text-xl ${hit ? "text-emerald-700" : "text-slate-900"}`}>{value}</span>
        {target != null && <span className="text-xs text-slate-400 font-mono-plex">/ {target}</span>}
        {!hit && target != null && planType && <Link to={`/planning?type=${planType}&date=${start}`} className="ml-1 text-[10px] font-mono-plex text-indigo-700 hover:underline" data-testid={`${testId}-plan`} title="Plan this activity">plan →</Link>}
      </div>
      {pct != null && <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-1 w-24"><div className={`h-full ${hit ? "bg-emerald-600" : tone}`} style={{ width: `${pct}%` }} /></div>}
      {sub && <div className="text-[10px] text-slate-500 font-mono-plex mt-1">{sub}</div>}
      {planned != null && planned > 0 && <div className="text-[10px] text-slate-400 font-mono-plex">{planned} planned</div>}
    </div>
  );
}

const TARGET_FIELDS = [["active_visits", "Active facility visits"], ["prospect_visits", "Prospect / churn visits"], ["provider_meetings", "Provider meetings"], ["followups", "Follow-ups completed"], ["marketing", "Marketing activities"]];

function TargetsDialog({ open, onOpenChange, onSaved, reps }) {
  const [data, setData] = useState(null);
  const [scope, setScope] = useState("all");
  const [t, setT] = useState(null);
  const load = () => api.get("/settings/worksheet-targets").then(({ data }) => setData(data));
  useEffect(() => { if (open) { setScope("all"); load(); } }, [open]);
  useEffect(() => { if (data) setT(scope === "all" ? data.default : (data.overrides[scope] || data.default)); }, [data, scope]);
  const hasOverride = scope !== "all" && !!data?.overrides?.[scope];
  const repName = reps.find(r => r.user_id === scope)?.name;
  const save = async () => {
    try {
      await api.put("/settings/worksheet-targets", t, { params: scope !== "all" ? { rep_id: scope } : {} });
      toast.success(scope === "all" ? "Default targets updated for all reps" : `Individual targets set for ${repName}`); onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const reset = async () => {
    try { await api.delete(`/settings/worksheet-targets/${scope}`); toast.success(`${repName} now follows the team default`); load(); onSaved?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="targets-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Target className="w-5 h-5" /> Weekly targets</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2">
          <Label className="shrink-0">Apply to</Label>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="h-9" data-testid="targets-scope"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="targets-scope-all">All reps (team default)</SelectItem>
              {reps.map(r => <SelectItem key={r.user_id} value={r.user_id} data-testid={`targets-scope-${r.user_id}`}>{r.name}{data?.overrides?.[r.user_id] ? " · custom" : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-slate-500" data-testid="targets-scope-hint">
          {scope === "all" ? "Sets the default for every rep without an individual target. Monthly and quarterly views scale these by the weeks in the period." : hasOverride ? `${repName} has individual targets that replace the team default.` : `${repName} currently follows the team default — editing creates individual targets.`}
        </p>
        {t && (
          <div className="grid gap-2">
            <div className="grid grid-cols-[1fr_72px_64px_64px] gap-2 text-[10px] uppercase tracking-widest font-mono-plex text-slate-400 px-1"><span /><span>Weekly</span><span>Monthly</span><span>Quarterly</span></div>
            {TARGET_FIELDS.map(([k, l]) => (
              <div key={k} className="grid grid-cols-[1fr_72px_64px_64px] gap-2 items-center px-1">
                <Label className="text-sm">{l}</Label>
                <Input type="number" min="0" className="h-8" value={t[k]} onChange={e => setT({ ...t, [k]: Number(e.target.value) })} data-testid={`target-${k}`} />
                <span className="text-sm font-mono-plex text-slate-600" data-testid={`target-${k}-monthly`}>{Math.round(t[k] * 4.33)}</span>
                <span className="text-sm font-mono-plex text-slate-600" data-testid={`target-${k}-quarterly`}>{Math.round(t[k] * 13)}</span>
              </div>
            ))}
          </div>
        )}
        <DialogFooter className="gap-2">
          {hasOverride && <Button variant="ghost" onClick={reset} className="mr-auto text-rose-700 hover:text-rose-800" data-testid="targets-reset">Reset to team default</Button>}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="targets-save">{scope === "all" ? "Save for all reps" : `Save for ${repName || "rep"}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RepWorksheet() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [period, setPeriod] = useState("week");
  const [start, setStart] = useState(new Date().toISOString().slice(0, 10));
  const { repId, setRepId, reps } = useRepFilter();
  const [ws, setWs] = useState(null);
  const [targetsOpen, setTargetsOpen] = useState(false);

  const load = () => api.get("/worksheet", { params: { period, start, rep_id: isAdmin ? repId : undefined } }).then(({ data }) => setWs(data)).catch(() => setWs(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, start, repId]);

  const exportXlsx = async () => {
    const token = localStorage.getItem("aceh_token") || "";
    const qs = new URLSearchParams({ period, start, ...(isAdmin ? { rep_id: repId } : {}) });
    const r = await fetch(`${API}/worksheet/export.xlsx?${qs}`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const blob = await r.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `rep-worksheet-${period}-${start}.xlsx`; a.click(); URL.revokeObjectURL(url);
  };

  const rows = ws?.rows || [];
  const showTeam = isAdmin && rows.length > 1;
  const allRisk = rows.flatMap(r => r.at_risk.map(a => ({ ...a, rep_name: r.rep_name })));
  const allMkt = rows.flatMap(r => r.marketing.map(m => ({ ...m, rep_name: r.rep_name })));

  return (
    <div className="mt-10" data-testid="rep-worksheet">
      <div className="mb-4 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500">Consolidated worksheet</div>
          <h2 className="font-heading text-2xl md:text-3xl font-black text-slate-900 mt-1 flex items-center gap-2"><CalendarDays className="w-6 h-6" /> Rep scorecard · {PERIOD_LABEL[period]}</h2>
          <p className="text-slate-500 mt-1 text-sm">Visits vs target, provider meetings, at-risk escalations, follow-ups and marketing — pulled from every tab. Weekly, monthly or quarterly; targets scale with the period. Streak = consecutive weeks hitting every target. Reps get this emailed every Monday. <Link to="/planning" className="underline" data-testid="ws-planning-link">Plan the week →</Link></p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Tabs value={period} onValueChange={setPeriod}><TabsList><TabsTrigger value="week" data-testid="ws-period-week">Weekly</TabsTrigger><TabsTrigger value="month" data-testid="ws-period-month">Monthly</TabsTrigger><TabsTrigger value="quarter" data-testid="ws-period-quarter">Quarterly</TabsTrigger></TabsList></Tabs>
          <div className="flex items-center border border-slate-200 rounded-md bg-white">
            <button className="p-2 hover:bg-slate-50" onClick={() => setStart(shift(start, period, -1))} data-testid="ws-prev"><ChevronLeft className="w-4 h-4" /></button>
            <span className="px-2 text-xs font-mono-plex min-w-[150px] text-center" data-testid="ws-range">{ws ? fmtRange(ws) : "…"}</span>
            <button className="p-2 hover:bg-slate-50" onClick={() => setStart(shift(start, period, 1))} data-testid="ws-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <Button variant="outline" size="sm" onClick={() => setStart(new Date().toISOString().slice(0, 10))} data-testid="ws-today">Today</Button>
          {isAdmin && (
            <Select value={repId} onValueChange={setRepId}>
              <SelectTrigger className="w-44 h-9" data-testid="ws-rep"><SelectValue placeholder="All reps" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All reps</SelectItem>{reps.map(r => <SelectItem key={r.user_id} value={r.user_id}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {isAdmin && <Button variant="outline" size="sm" onClick={() => setTargetsOpen(true)} data-testid="ws-targets-btn"><Settings2 className="w-4 h-4 mr-1" /> Targets</Button>}
          <Button variant="outline" size="sm" onClick={exportXlsx} data-testid="ws-export"><Download className="w-4 h-4 mr-1" /> Excel</Button>
          <Button variant="outline" size="sm" onClick={async () => { try { const { data } = await api.post("/worksheet/send-my-scorecard"); toast.success(`Scorecard sent to ${data.sent_to}`); } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); } }} data-testid="ws-email-me"><Mail className="w-4 h-4 mr-1" /> Email me</Button>
        </div>
      </div>

      {ws && <ScoreTiles ws={ws} rows={rows} isAdmin={isAdmin} userId={user?.user_id} />}

      {ws && (
        <Card className="border-slate-200 shadow-none overflow-x-auto">
          <table className="w-full text-sm" data-testid="ws-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest font-mono-plex text-slate-500">
              <tr><th className="p-3">Rep</th>{METRICS.map(m => <th key={m.key} className="p-3">{m.label}{m.target && <div className="text-[9px] normal-case tracking-normal text-slate-400">default target {ws.targets[m.target]}</div>}</th>)}</tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">No reps found.</td></tr>}
              {rows.map(r => (
                <tr key={r.rep_id} className="border-t border-slate-100 row-enter" data-testid={`ws-row-${r.rep_id}`}>
                  <td className="p-3">
                    <div className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">{r.rep_name}
                      {r.streak > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 border border-orange-200 text-[10px] font-mono-plex font-bold" data-testid={`ws-streak-${r.rep_id}`} title="Consecutive weeks hitting every target"><Flame className="w-3 h-3" /> {r.streak}-wk streak</span>}
                      {r.targets?.custom && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200 text-[10px] font-mono-plex font-bold" data-testid={`ws-custom-targets-${r.rep_id}`} title="Individual targets set by admin"><Target className="w-3 h-3" /> custom targets</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono-plex">{r.facilities_assigned} facilities · {r.visits_total} visits</div>
                  </td>
                  {METRICS.map(m => <td key={m.key} className="p-3 align-top"><MetricCell value={r[m.key]} target={m.target ? r.targets?.[m.target] ?? ws.targets[m.target] : null} tone={m.tone} sub={m.sub?.(r)} testId={`ws-${r.rep_id}-${m.key}`} planned={r.planned?.[m.key]} planType={(!isAdmin || r.rep_id === user?.user_id) ? m.plan : null} start={ws.start} /></td>)}
                </tr>
              ))}
              {showTeam && (
                <tr className="border-t-2 border-slate-900 bg-slate-50 font-semibold" data-testid="ws-team-row">
                  <td className="p-3"><div className="text-slate-900">Team total</div><div className="text-[11px] text-slate-500 font-mono-plex font-normal">{ws.team.facilities_assigned} facilities · {ws.team.visits_total} visits</div></td>
                  {METRICS.map(m => <td key={m.key} className="p-3 align-top"><MetricCell value={ws.team[m.key]} target={m.target ? ws.team_targets?.[m.target] ?? ws.targets[m.target] * rows.length : null} tone={m.tone} sub={m.sub?.(ws.team)} /></td>)}
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      )}

      {ws && (allRisk.length > 0 || allMkt.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          {allRisk.length > 0 && (
            <Card className="p-5 border-slate-200 shadow-none" data-testid="ws-risk-list">
              <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500 flex items-center gap-1 mb-2"><ShieldAlert className="w-3 h-3 text-rose-600" /> At-risk escalations this {period}</div>
              <div className="divide-y divide-slate-100">
                {allRisk.map((a, i) => (
                  <div key={i} className="py-2 flex items-start justify-between gap-2">
                    <div className="min-w-0"><Link to={`/facilities/${a.facility_id}`} className="text-sm font-medium text-slate-900 hover:underline">{a.name}</Link><div className="text-xs text-slate-500 truncate">{a.reason}</div><div className="text-[10px] text-slate-400 font-mono-plex">{a.rep_name} · {(a.at || "").slice(0, 10)}</div></div>
                    <div className="flex flex-col items-end gap-1 shrink-0"><Badge variant="outline" className={`${RISK_TONE[a.level]} text-[10px]`}>{a.level_label}</Badge>{a.acknowledged && <span className="text-[9px] font-mono-plex text-emerald-700" data-testid="ws-risk-acked">acknowledged</span>}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {allMkt.length > 0 && (
            <Card className="p-5 border-slate-200 shadow-none" data-testid="ws-marketing-list">
              <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500 flex items-center gap-1 mb-2"><Megaphone className="w-3 h-3 text-sky-600" /> Marketing this {period} · {money(ws.team.marketing_spent)} spent · {money(ws.team.marketing_revenue)} revenue · {ws.team.marketing_attendees} attendees</div>
              <div className="divide-y divide-slate-100">
                {allMkt.map((m, i) => (
                  <div key={i} className="py-2 flex items-start justify-between gap-2">
                    <div className="min-w-0"><Link to={`/facilities/${m.facility_id}`} className="text-sm font-medium text-slate-900 hover:underline">{m.title}</Link><div className="text-xs text-slate-500">{m.facility_name} · {m.rep_name}</div></div>
                    <div className="text-right text-xs font-mono-plex shrink-0"><div>{m.activity_date}</div><div className="text-slate-500">{money(m.amount_spent)} · {m.attendees ?? 0} ppl</div>{m.next_visit_date && <div className="text-sky-700">next {m.next_visit_date}</div>}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      <TargetsDialog open={targetsOpen} onOpenChange={setTargetsOpen} onSaved={load} reps={reps} />
    </div>
  );
}
